import React, { useMemo } from 'react';
import { SingleTabSubViewJson } from '../../../utils/constants/ReportTabsType';
import { Box } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { PLOT_TITLES } from '../../../utils/constants/ReportTabs';
import TitleWithHorizontalDivider from '../../atom/TitleWithHorizontalDivider/TitleWithHorizontalDivider';

/**
 *
 * @param {React.PropsWithChildren<ISubViewBarStacked>} props
 * @return {JSX.Element}
 * @constructor
 */
const SubViewBarStacked: React.FC<ISubViewBarStacked> = ({
                                                           data,
                                                           targetGroupName,
                                                           pythonElementName,
                                                         }) => {
  // ✅ Plain value, no hooks
  const isFileNotFound =
      data.data.length > 0 && data.data[0].Type === 'File Not Found';

  const labels = useMemo(() => {
    if (isFileNotFound) return [];
    if (data.data.length === 0) return [];

    const dataKeys = Object.keys(data.data[0]);
    return dataKeys.filter(
        (item) => !['Target name', 'Type'].includes(item)
    );
  }, [data.data, isFileNotFound]);

  const datasets = useMemo(() => {
    if (isFileNotFound) return [];
    if (data.data.length === 0) return [];

    let linesToTake = data.data.filter(
        (line) => line['Target name'] === targetGroupName
    );
    if (linesToTake.length === 0) linesToTake = data.data;

    const datasets: { label: string; data: any[]; backgroundColor: string }[] =
        [];

    for (const r of linesToTake) {
      let datasetName = '';
      const datasetValues: any[] = [];

      for (const key in r) {
        const value = r[key];
        if (key === 'Type') datasetName = value as string;
        else if (key === 'Target name') continue;
        else datasetValues.push(value);
      }

      let color = data.label_metadata[datasetName]?.color;
      if (!color) {
        color = `#${Math.floor(Math.random() * 0xffffff)
            .toString(16)
            .padStart(6, '0')}`;
        console.error(
            `I've picked up the color cause no color were set for ${datasetName} inside ${targetGroupName}`
        );
      }

      datasets.push({
        label: datasetName,
        data: datasetValues,
        backgroundColor: color,
      });
    }

    return datasets;
  }, [data.data, targetGroupName, isFileNotFound, labels]);


  // ✅ Now it's safe to return conditionally: hooks already ran
  if (isFileNotFound) {
    return null; // or some message if you prefer
  }


  const chartData = {
    // turn "27-01-2025\n02-02-2025" into ["27-01-2025", "02-02-2025"]
    labels: labels.map(label =>
        typeof label === 'string' ? label.split('\n') : label
    ),
    datasets,
  };


  return (
      <Box>
        {PLOT_TITLES[pythonElementName] &&
        PLOT_TITLES[pythonElementName].title ? (
            <Box mb={1}>
              <TitleWithHorizontalDivider
                  name={PLOT_TITLES[pythonElementName].title as string}
              />
            </Box>
        ) : (
            ''
        )}
        <Bar
            data={chartData}
            options={{
              responsive: true,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              scales: {
                x: {
                  stacked: true,
                  title: {
                    display: !!PLOT_TITLES[pythonElementName].axis_x,
                    text: PLOT_TITLES[pythonElementName].axis_x as string,
                    font: {
                      size: 16,
                    },
                  },
                },
                y: {
                  stacked: true,
                  title: {
                    display: !!PLOT_TITLES[pythonElementName].axis_y,
                    text: PLOT_TITLES[pythonElementName].axis_y as string,
                    font: {
                      size: 16,
                    },
                  },
                },
              },
              plugins: {
                legend: {
                  position: 'right',
                  reverse: true,
                  maxWidth: 290,
                  labels: {
                    font: {
                      size: 14,
                    },
                    usePointStyle: true,
                    pointStyle: 'rectRounded',
                    padding: 10,
                    boxWidth: 15,
                  },
                },
                zoom: {
                  zoom: {
                    wheel: {
                      enabled: true,
                    },
                    pinch: {
                      enabled: true,
                    },
                    mode: 'x',
                  },
                },
              },
            }}
        />
      </Box>
  );
};

export interface ISubViewBarStacked {
  data: SingleTabSubViewJson;
  pythonElementName: string; // represent the python_element name that identifies uniquely a plot/table among all dashboards - like in element_config.json
  targetGroupName?: string | undefined;
}

export default SubViewBarStacked;